import { CarePlanResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  SwitchV2,
  Text,
} from '@equip.health/ui';
import Loading from '~/pages/loading';
import { CarePlanInterventionCollapse } from '~/components/maud/patients/profile/tabs/careplan/CarePlanInterventionCollapse';
import { useInterventionsGroupedByPhaseStage } from '~/lib/hooks/useInterventionsGroupedByPhaseStage';
import { CARE_PLAN_INTERVENTION_SECTION_LABELS } from '~/lib/constants/carePlan.constants';
import { useGetPhaseStageTreatmentAttributesQuery } from '~/store/services/treatmentAttributes';
import { useState } from 'react';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { AddCustomInterventionModal } from '~/components/maud/patients/profile/tabs/careplan/AddCustomInterventionModal';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  treatmentEpisodeId: string;
  carePlan: CarePlanResponse;
  hasModalityChanged: boolean;
};

export const ActiveCarePlanTreatmentStagesSection = ({
  treatmentEpisodeId,
  carePlan,
  hasModalityChanged,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { currentUser } = useGlobalContext();
  const { carePlanM4CustomInterventions } = useFlags<LDFlags>();

  const [isAssignedToMeEnabled, setIsAssignedToMeEnabled] = useState(false);
  const [isAddInterventionModalOpen, setIsAddInterventionModalOpen] =
    useState(false);

  const {
    interventionsByPhaseStage,
    customInterventions: otherInterventions,
    isLoading: isGetInterventionsByPhaseStageLoading,
  } = useInterventionsGroupedByPhaseStage(
    treatmentEpisodeId,
    carePlan,
    isAssignedToMeEnabled ? [currentUser.providerTypeDetails.name] : undefined,
    carePlanM4CustomInterventions,
  );
  const {
    data: phaseStageTreatmentAttributes,
    isLoading: isPhaseStageTreatmentAttributesLoading,
  } = useGetPhaseStageTreatmentAttributesQuery(treatmentEpisodeId);

  if (
    isGetInterventionsByPhaseStageLoading ||
    isPhaseStageTreatmentAttributesLoading
  ) {
    return <Loading />;
  }

  const phasesOrStages = Object.keys(interventionsByPhaseStage).some((key) =>
    key.includes('Stage'),
  )
    ? 'stages'
    : 'phases';

  const activePhaseStage = phaseStageTreatmentAttributes?.find(
    (attribute) => !attribute.endDate,
  );

  return (
    <ChakraBox marginTop="36px">
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="12px"
      >
        <Text variant="h5">
          {carePlanM4CustomInterventions
            ? 'Interventions'
            : `Treatment ${phasesOrStages}`}
        </Text>
        {carePlanM4CustomInterventions && (
          <FireflyButton
            id="add-interventions-btn"
            isDisabled={hasModalityChanged}
            leftIcon={FAIconName.plusRegular}
            onClick={() => {
              trackEvent(
                CarePlanEvents.clickOnAddInterventionsButtonInTreatmentPhaseStageComponent,
              );
              setIsAddInterventionModalOpen(true);
            }}
          >
            Add interventions
          </FireflyButton>
        )}
      </ChakraFlex>
      <ChakraFlex alignItems="center" gap="12px" marginBottom="22px">
        <SwitchV2
          id="care-plan-assigned-to-me-switch"
          isChecked={isAssignedToMeEnabled}
          onChange={() => {
            trackEvent(
              isAssignedToMeEnabled
                ? CarePlanEvents.toggleOffAssignedToMeButtonInTreatmentPhaseStageComponent
                : CarePlanEvents.toggleOnAssignedToMeButtonInTreatmentPhaseStageComponent,
            );
            setIsAssignedToMeEnabled((previous) => !previous);
          }}
        />
        <Text>Assigned to me</Text>
      </ChakraFlex>
      {Object.entries(interventionsByPhaseStage).map(
        ([label, interventions]) => (
          <CarePlanInterventionCollapse
            interventions={interventions}
            isAssignedToMeEnabled={isAssignedToMeEnabled}
            key={label}
            label={label}
            openByDefault={label === activePhaseStage?.phaseStage?.label}
            phaseOrStageClassification={
              phasesOrStages === 'stages' ? 'STAGE' : 'PHASE'
            }
            treatmentEpisodeId={treatmentEpisodeId}
          />
        ),
      )}
      {carePlanM4CustomInterventions && (
        <CarePlanInterventionCollapse
          interventions={otherInterventions}
          isAllOtherInterventions
          isAssignedToMeEnabled={isAssignedToMeEnabled}
          key="all-other-interventions"
          label={CARE_PLAN_INTERVENTION_SECTION_LABELS.ALL_OTHER_INTERVENTIONS}
          openByDefault={false}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
      {isAddInterventionModalOpen && (
        <AddCustomInterventionModal
          carePlan={carePlan}
          onClose={() => setIsAddInterventionModalOpen(false)}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
    </ChakraBox>
  );
};
